import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Plus, ChevronLeft, ChevronRight, Clock, MapPin, User, Home, GripVertical, Trash2, Pencil, Calendar as CalendarIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Schedule = ({ isAdmin = false }) => {
  const { user } = useAuth();
  const [visits, setVisits] = useState([]);
  const [listings, setListings] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentWeekStart, setCurrentWeekStart] = useState(getWeekStart(new Date()));
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVisit, setEditingVisit] = useState(null);
  const [draggedVisit, setDraggedVisit] = useState(null);
  const [formData, setFormData] = useState({
    listing_id: '',
    customer_id: '',
    assigned_to: '',
    visit_date: '',
    visit_time: '10:00',
    duration_minutes: 60,
    status: 'scheduled',
    notes: '',
    visit_type: 'showing'
  });

  function getWeekStart(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  }

  function formatDate(date) {
    return date.toISOString().split('T')[0];
  }

  function getWeekDays() {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(currentWeekStart);
      day.setDate(day.getDate() + i);
      days.push(day);
    }
    return days;
  }

  const weekDays = getWeekDays();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Calculate week days inside callback to avoid dependency issues
      const days = [];
      for (let i = 0; i < 7; i++) {
        const day = new Date(currentWeekStart);
        day.setDate(currentWeekStart.getDate() + i);
        days.push(day);
      }
      
      const startDate = formatDate(currentWeekStart);
      const endDate = formatDate(days[6]);
      
      const [visitsRes, listingsRes, customersRes] = await Promise.all([
        axios.get(`${API}/visits/?start_date=${startDate}&end_date=${endDate}`),
        axios.get(`${API}/listings/`),
        axios.get(`${API}/customers/`)
      ]);
      
      setVisits(visitsRes.data);
      setListings(listingsRes.data);
      setCustomers(customersRes.data);
      
      if (isAdmin) {
        const employeesRes = await axios.get(`${API}/users/employees`);
        setEmployees(employeesRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  }, [currentWeekStart, isAdmin]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // For employees, auto-assign to themselves
      const submitData = { ...formData };
      if (!isAdmin && user) {
        submitData.assigned_to = user.id;
      }
      
      if (editingVisit) {
        await axios.put(`${API}/visits/${editingVisit.id}`, submitData);
        toast.success('Visit updated successfully');
      } else {
        await axios.post(`${API}/visits/`, submitData);
        toast.success('Visit scheduled successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save visit');
    }
  };

  const handleEdit = (visit) => {
    setEditingVisit(visit);
    setFormData({
      listing_id: visit.listing_id,
      customer_id: visit.customer_id || '',
      assigned_to: visit.assigned_to,
      visit_date: visit.visit_date,
      visit_time: visit.visit_time,
      duration_minutes: visit.duration_minutes,
      status: visit.status,
      notes: visit.notes || '',
      visit_type: visit.visit_type
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this visit?')) return;
    
    try {
      await axios.delete(`${API}/visits/${id}`);
      toast.success('Visit deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete visit');
    }
  };

  const resetForm = () => {
    setEditingVisit(null);
    setFormData({
      listing_id: '',
      customer_id: '',
      assigned_to: '',
      visit_date: '',
      visit_time: '10:00',
      duration_minutes: 60,
      status: 'scheduled',
      notes: '',
      visit_type: 'showing'
    });
  };

  const handleDragStart = (e, visit) => {
    setDraggedVisit(visit);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetDate, targetEmployeeId = null) => {
    e.preventDefault();
    if (!draggedVisit || !isAdmin) return;
    
    const newDate = formatDate(targetDate);
    const updateData = { visit_date: newDate };
    
    if (targetEmployeeId && targetEmployeeId !== draggedVisit.assigned_to) {
      updateData.assigned_to = targetEmployeeId;
    }
    
    try {
      await axios.put(`${API}/visits/${draggedVisit.id}`, updateData);
      toast.success('Visit rescheduled');
      fetchData();
    } catch (error) {
      toast.error('Failed to reschedule visit');
    }
    
    setDraggedVisit(null);
  };

  const previousWeek = () => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(newStart.getDate() - 7);
    setCurrentWeekStart(newStart);
  };

  const nextWeek = () => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(newStart.getDate() + 7);
    setCurrentWeekStart(newStart);
  };

  const goToToday = () => {
    setCurrentWeekStart(getWeekStart(new Date()));
  };

  const getVisitsForDay = (date) => {
    const dateStr = formatDate(date);
    return visits.filter(v => v.visit_date === dateStr);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-600 text-white';
      case 'cancelled': return 'bg-red-500 text-white';
      case 'no_show': return 'bg-gray-500 text-white';
      default: return 'bg-brand-gold text-brand-brown';
    }
  };

  const getVisitTypeLabel = (type) => {
    switch (type) {
      case 'showing': return 'Showing';
      case 'open_house': return 'Open House';
      case 'inspection': return 'Inspection';
      case 'follow_up': return 'Follow Up';
      default: return type;
    }
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-foreground">
            Visit Schedule
          </h1>
          <p className="font-body text-muted-foreground mt-1">
            {isAdmin ? 'Drag and drop to reschedule visits' : 'Manage your scheduled visits'}
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="rounded-none font-heading font-bold uppercase tracking-wide bg-brand-gold text-brand-brown hover:bg-brand-gold/90" data-testid="schedule-visit-btn">
              <Plus className="w-4 h-4 mr-2" />
              {isAdmin ? 'Schedule Visit' : 'Add Appointment'}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg" aria-describedby="schedule-dialog-description">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl font-bold">
                {editingVisit ? 'Edit Visit' : (isAdmin ? 'Schedule New Visit' : 'Add New Appointment')}
              </DialogTitle>
              <p id="schedule-dialog-description" className="sr-only">
                {editingVisit ? 'Edit the selected visit details' : 'Schedule a new property visit'}
              </p>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                  Property *
                </Label>
                <Select value={formData.listing_id} onValueChange={(value) => setFormData({ ...formData, listing_id: value })}>
                  <SelectTrigger className="rounded-none border-2" data-testid="visit-listing">
                    <SelectValue placeholder="Select property..." />
                  </SelectTrigger>
                  <SelectContent>
                    {listings.filter(l => l.status === 'available').map((listing) => (
                      <SelectItem key={listing.id} value={listing.id}>{listing.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {isAdmin && (
                <div className="space-y-2">
                  <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Assign To Employee *
                  </Label>
                  <Select value={formData.assigned_to} onValueChange={(value) => setFormData({ ...formData, assigned_to: value })}>
                    <SelectTrigger className="rounded-none border-2" data-testid="visit-employee">
                      <SelectValue placeholder="Select employee..." />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

                <div className="space-y-2">
                  <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Customer (Optional)
                  </Label>
                  <Select value={formData.customer_id || "none"} onValueChange={(value) => setFormData({ ...formData, customer_id: value === "none" ? "" : value })}>
                    <SelectTrigger className="rounded-none border-2" data-testid="visit-customer">
                      <SelectValue placeholder="Select customer..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No customer</SelectItem>
                      {customers.map((customer) => (
                        <SelectItem key={customer.id} value={customer.id}>
                          {customer.first_name} {customer.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                      Date *
                    </Label>
                    <Input
                      type="date"
                      value={formData.visit_date}
                      onChange={(e) => setFormData({ ...formData, visit_date: e.target.value })}
                      required
                      className="rounded-none border-2"
                      data-testid="visit-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                      Time *
                    </Label>
                    <Input
                      type="time"
                      value={formData.visit_time}
                      onChange={(e) => setFormData({ ...formData, visit_time: e.target.value })}
                      required
                      className="rounded-none border-2"
                      data-testid="visit-time"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                      Visit Type
                    </Label>
                    <Select value={formData.visit_type} onValueChange={(value) => setFormData({ ...formData, visit_type: value })}>
                      <SelectTrigger className="rounded-none border-2" data-testid="visit-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="showing">Showing</SelectItem>
                        <SelectItem value="open_house">Open House</SelectItem>
                        <SelectItem value="inspection">Inspection</SelectItem>
                        <SelectItem value="follow_up">Follow Up</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                      Duration
                    </Label>
                    <Select value={formData.duration_minutes.toString()} onValueChange={(value) => setFormData({ ...formData, duration_minutes: parseInt(value) })}>
                      <SelectTrigger className="rounded-none border-2" data-testid="visit-duration">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="30">30 min</SelectItem>
                        <SelectItem value="60">1 hour</SelectItem>
                        <SelectItem value="90">1.5 hours</SelectItem>
                        <SelectItem value="120">2 hours</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {editingVisit && (
                  <div className="space-y-2">
                    <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                      Status
                    </Label>
                    <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                      <SelectTrigger className="rounded-none border-2" data-testid="visit-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="scheduled">Scheduled</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                        <SelectItem value="no_show">No Show</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Notes
                  </Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={2}
                    className="rounded-none border-2"
                    data-testid="visit-notes"
                  />
                </div>

                <div className="flex gap-4 pt-4">
                  <Button type="submit" className="rounded-none font-heading font-bold uppercase tracking-wide bg-brand-gold text-brand-brown hover:bg-brand-gold/90" data-testid="save-visit-btn">
                    {editingVisit ? 'Update Visit' : (isAdmin ? 'Schedule Visit' : 'Add Appointment')}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} className="rounded-none border-2">
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
      </div>

      {/* Week Navigation */}
      <div className="flex items-center justify-between bg-white p-4 border border-border">
        <Button variant="outline" onClick={previousWeek} className="rounded-none border-2">
          <ChevronLeft className="w-4 h-4 mr-1" />
          Previous
        </Button>
        <div className="flex items-center gap-4">
          <h2 className="font-heading text-lg font-semibold">
            {weekDays[0].toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </h2>
          <Button variant="outline" onClick={goToToday} className="rounded-none border-2 text-xs">
            Today
          </Button>
        </div>
        <Button variant="outline" onClick={nextWeek} className="rounded-none border-2">
          Next
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>

      {/* Calendar Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-brand-gold border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="grid grid-cols-7 gap-2">
          {weekDays.map((day, index) => (
            <div 
              key={index}
              className={`min-h-[300px] border ${isToday(day) ? 'border-brand-gold border-2' : 'border-border'} bg-white`}
              onDragOver={isAdmin ? handleDragOver : undefined}
              onDrop={isAdmin ? (e) => handleDrop(e, day) : undefined}
            >
              <div className={`p-3 border-b ${isToday(day) ? 'bg-brand-gold/10' : 'bg-secondary/50'}`}>
                <p className="font-heading text-xs uppercase tracking-widest text-muted-foreground">
                  {day.toLocaleDateString('en-US', { weekday: 'short' })}
                </p>
                <p className={`font-heading text-xl font-bold ${isToday(day) ? 'text-brand-gold' : ''}`}>
                  {day.getDate()}
                </p>
              </div>
              <div className="p-2 space-y-2">
                {getVisitsForDay(day).map((visit) => (
                  <div
                    key={visit.id}
                    draggable={isAdmin}
                    onDragStart={isAdmin ? (e) => handleDragStart(e, visit) : undefined}
                    className={`p-2 border-l-4 ${getStatusColor(visit.status).replace('text-', 'border-').split(' ')[0]} bg-secondary/30 hover:bg-secondary/50 cursor-${isAdmin ? 'grab' : 'default'} transition-colors group`}
                    data-testid={`visit-card-${visit.id}`}
                  >
                    <div className="flex items-start justify-between gap-1">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
                          <Clock className="w-3 h-3" />
                          <span>{visit.visit_time}</span>
                          <Badge className={`rounded-none text-[8px] uppercase ml-1 ${getStatusColor(visit.status)}`}>
                            {visit.status}
                          </Badge>
                        </div>
                        <p className="font-heading text-sm font-semibold truncate" title={visit.listing_title}>
                          {visit.listing_title}
                        </p>
                        {visit.assigned_to_name && (
                          <p className="font-body text-xs text-muted-foreground flex items-center gap-1 truncate">
                            <User className="w-3 h-3" />
                            {visit.assigned_to_name}
                          </p>
                        )}
                        {visit.customer_name && (
                          <p className="font-body text-xs text-brand-gold truncate">
                            Client: {visit.customer_name}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {isAdmin && <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab" />}
                        <button onClick={() => handleEdit(visit)} className="p-1 hover:bg-white rounded" data-testid={`edit-visit-${visit.id}`}>
                          <Pencil className="w-3 h-3" />
                        </button>
                        <button onClick={() => handleDelete(visit.id)} className="p-1 hover:bg-white rounded text-destructive" data-testid={`delete-visit-${visit.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {getVisitsForDay(day).length === 0 && (
                  <p className="text-xs text-muted-foreground text-center py-4">No visits</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <span className="font-heading text-xs uppercase tracking-widest text-muted-foreground">Status:</span>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-brand-gold"></div>
          <span>Scheduled</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-green-600"></div>
          <span>Completed</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-red-500"></div>
          <span>Cancelled</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-gray-500"></div>
          <span>No Show</span>
        </div>
      </div>
    </div>
  );
};

export default Schedule;
